// Shared WASM linear-memory helpers used by both MunosServer and
// MunosDummyClient. Both sides instantiate the same compiled wasm module
// and follow the same bump-allocator / array-header layout, so the
// encoding logic must stay byte-identical. Drift here causes very subtle
// data-corruption bugs.
//
// Hosts (server, dummy client) implement the small `HeapHost` interface
// by exposing their `memory`/`view`/`heapPtrGlobal` fields plus a
// `refreshView()` method that rebinds the cached DataView after a grow.
// The helpers below take that host and operate on its memory directly,
// so callers keep their existing `this.view` field references valid.
//
// `refreshView()` is required because `WebAssembly.Memory.grow()` detaches
// the previous ArrayBuffer — every DataView pointing at it stops working.
//
// Array layout:
//
//   offset  0:  ptr       i32  → element data (separate allocation)
//   offset  4:  size      i32    capacity
//   offset  8:  length    i32    current element count
//   offset 12:  refcount  i32    reserved for ref-counted lifetime (1
//                                at allocation today)
//
// Element data lives in a second bump-allocated block; the header's ptr
// field addresses it. Strings reuse this header with elementSize = 1.
import { MAX_HEAP_BYTES, MunosResourceError } from "../limits.js";
export const HEADER_SIZE = 16;
export const OFF_PTR = 0;
export const OFF_SIZE = 4;
export const OFF_LENGTH = 8;
export const OFF_REFCOUNT = 12;
// Bump-allocate `bytes` bytes from the wasm-side heap, growing memory in
// 64 KiB pages as needed. Returns a 4-byte-aligned pointer into the
// returned region. The script's allocator follows the same alignment, so
// host-allocated and script-allocated objects can be interleaved safely.
export function bump(h, bytes) {
    const cur = h.heapPtrGlobal.value;
    const aligned = (cur + 3) & ~3;
    const next = aligned + bytes;
    // Resource limit: refuse to grow the heap past the ceiling (docs/trust-model.md
    // — the engine's own sane bound). This is the single choke point for ALL script
    // allocation, so it also caps a decompression bomb. `bytes < 0` is a defensive
    // guard: a negative size would make `next < aligned`, slipping under the cap
    // and corrupting the heap. Enforced identically on every tier for determinism.
    if (bytes < 0 || next > MAX_HEAP_BYTES) {
        throw new MunosResourceError(`munos: heap allocation of ${bytes} bytes (to ${next}) exceeds the ${MAX_HEAP_BYTES}-byte limit`);
    }
    while (next > h.memory.buffer.byteLength) {
        h.memory.grow(1);
        h.refreshView();
    }
    h.heapPtrGlobal.value = next;
    return aligned;
}
// --- young-generation rewind (docs/memory-model.md §"Reclamation, staged") ---
//
// The bump allocator never frees, so a script that allocates scratch every
// event grows the heap without bound. The cheap reclaim for that — used
// around a *rewindable* event, one the compiler proved never lets a heap
// allocation escape into a global — is to snapshot the cursor before the
// event and restore it after: every byte the event bump-allocated becomes
// free again in one move, with no per-object bookkeeping.
//
// This is only sound when nothing outlives the event still points above the
// mark. The compiler's rewindability analysis is what guarantees that; these
// two helpers are just the mechanism it drives.
// Snapshot the current heap cursor. Pair with heapRewind.
export function heapMark(h) {
    return h.heapPtrGlobal.value;
}
// Restore the heap cursor to a prior mark, freeing everything allocated
// since. `mark` must come from an earlier heapMark on the same heap, so it
// can only move the cursor backward (or leave it put); a mark above the
// current cursor would mean "un-freeing" memory that was never live and is
// rejected as a caller bug.
export function heapRewind(h, mark) {
    const cur = h.heapPtrGlobal.value;
    if (mark > cur) {
        throw new MunosResourceError(`munos: heapRewind to ${mark} above current cursor ${cur} — mark is not from this frame`);
    }
    h.heapPtrGlobal.value = mark;
}
// Field accessors — read length/size/data-ptr off an array header.
export function arrLength(h, headerPtr) {
    return h.view.getInt32(headerPtr + OFF_LENGTH, true);
}
export function arrSize(h, headerPtr) {
    return h.view.getInt32(headerPtr + OFF_SIZE, true);
}
export function arrData(h, headerPtr) {
    return h.view.getInt32(headerPtr + OFF_PTR, true);
}
// Allocate an array with the given length, capacity, and per-element
// size, zero-initialized. Returns the 16-byte header pointer; the data
// block lives at `arrData(h, ptr)`. Capacity defaults to `length`,
// matching the spec's initialization rule (sized declarations start
// filled).
export function allocArray(h, length, elementSize, capacity = length) {
    const header = bump(h, HEADER_SIZE);
    const dataBytes = capacity * elementSize;
    const data = bump(h, dataBytes);
    h.view.setInt32(header + OFF_PTR, data, true);
    h.view.setInt32(header + OFF_SIZE, capacity, true);
    h.view.setInt32(header + OFF_LENGTH, length, true);
    h.view.setInt32(header + OFF_REFCOUNT, 1, true);
    for (let i = 0; i < dataBytes; i++)
        h.view.setUint8(data + i, 0);
    return header;
}
// Encode a byte slice as a Munos u8[] and return its header pointer.
// Used for event params, handoff blobs, and host responses.
export function encodeBytes(h, bytes) {
    const ptr = allocArray(h, bytes.length, 1);
    // refreshView in case a grow ran during allocation
    const data = arrData(h, ptr);
    for (let i = 0; i < bytes.length; i++) {
        h.view.setUint8(data + i, bytes[i]);
    }
    return ptr;
}
// Read back the bytes at a Munos u8[] pointer. Returns a copy so callers
// don't hold a view into wasm memory across allocations (which could
// invalidate after a grow).
export function readBytesPtr(h, ptr) {
    const len = arrLength(h, ptr);
    const data = arrData(h, ptr);
    return new Uint8Array(h.memory.buffer, data, len).slice();
}
// Allocate a struct's inline footprint: bump-allocate `inlineSize`
// bytes, zero-filled. Returns the pointer. The caller (or codegen
// trampoline that follows) must initialize array-typed fields via
// initArrayField to set up their inline headers + data blocks.
export function allocStruct(h, inlineSize) {
    const ptr = bump(h, inlineSize);
    for (let i = 0; i < inlineSize; i++)
        h.view.setUint8(ptr + i, 0);
    return ptr;
}
// Initialize an array-typed struct field at `headerAddr` (an absolute
// address inside a struct's inline footprint). Writes the 16-byte array
// header in place and allocates a fresh data block sized for `length`
// elements of `elementBytes` each (zero-filled). The header's
// size/length both equal `length` so the field is indexable
// immediately, mirroring the standalone array rule.
export function initArrayField(h, headerAddr, length, elementBytes) {
    const dataBytes = length * elementBytes;
    const data = bump(h, dataBytes);
    h.view.setInt32(headerAddr + OFF_PTR, data, true);
    h.view.setInt32(headerAddr + OFF_SIZE, length, true);
    h.view.setInt32(headerAddr + OFF_LENGTH, length, true);
    h.view.setInt32(headerAddr + OFF_REFCOUNT, 1, true);
    for (let i = 0; i < dataBytes; i++)
        h.view.setUint8(data + i, 0);
}
// Image descriptors are the 8-byte blob every image_* builtin returns
// to the script: width i32 @ +0, height i32 @ +4, little-endian. The
// pointer doubles as the host-side key for whatever pixel store the
// embedding keeps (images Map, ghostImages, ...). Not an array header —
// no length/refcount fields.
export function allocImageDescriptor(h, width, height) {
    const ptr = bump(h, 8);
    h.view.setInt32(ptr, width, true);
    h.view.setInt32(ptr + 4, height, true);
    return ptr;
}
export function readImageDescriptor(h, ptr) {
    return {
        width: h.view.getInt32(ptr, true),
        height: h.view.getInt32(ptr + 4, true),
    };
}
// Single-level deep copy of a Munos array (or string). Allocates a
// fresh header + data block with the same length, size, and element
// type as the source, then byte-copies the data. The checker only emits
// a `copy` for flat primitive arrays and strings (composite-element
// arrays are rejected), so a single level IS the whole object here — no
// inner refs are shared. Full recursion lands when nested composites do.
export function copyArray(h, srcPtr, elementBytes) {
    const srcLen = arrLength(h, srcPtr);
    const srcSize = arrSize(h, srcPtr);
    const srcData = arrData(h, srcPtr);
    const dst = allocArray(h, srcLen, elementBytes, srcSize);
    const dstData = arrData(h, dst);
    const totalBytes = srcLen * elementBytes;
    for (let i = 0; i < totalBytes; i++) {
        h.view.setUint8(dstData + i, h.view.getUint8(srcData + i));
    }
    return dst;
}
// Concatenate two Munos arrays into a fresh array: length = a.length +
// b.length, a's elements then b's, independent backing data. Like copyArray
// this is a single-level byte copy — the checker only emits `concat` for flat
// primitive-element arrays (composite elements are rejected), so no inner refs
// are shared. Both operands must share the caller-supplied element stride
// (the checker guarantees matching element types). Data pointers are read
// BEFORE allocArray (a grow refreshes the view but not linear offsets).
export function concatArrays(h, aPtr, bPtr, elementBytes) {
    const aLen = arrLength(h, aPtr);
    const bLen = arrLength(h, bPtr);
    const aData = arrData(h, aPtr);
    const bData = arrData(h, bPtr);
    const dst = allocArray(h, aLen + bLen, elementBytes);
    const dstData = arrData(h, dst);
    const aBytes = aLen * elementBytes;
    for (let i = 0; i < aBytes; i++)
        h.view.setUint8(dstData + i, h.view.getUint8(aData + i));
    const bBytes = bLen * elementBytes;
    for (let i = 0; i < bBytes; i++)
        h.view.setUint8(dstData + aBytes + i, h.view.getUint8(bData + i));
    return dst;
}
// Read a Munos string pointer as a JS string.
export function readStringPtr(h, ptr) {
    const len = arrLength(h, ptr);
    const data = arrData(h, ptr);
    const bytes = new Uint8Array(h.memory.buffer, data, len);
    return new TextDecoder().decode(bytes);
}
// Allocate a Munos string from a JS string (utf-8). Strings share the
// 16-byte array header with u8[]; the data block holds the bytes.
export function allocString(h, s) {
    return encodeBytes(h, new TextEncoder().encode(s));
}
// Allocate a Munos string[] — an i32[] of pointers, each addressing a
// separately allocated string header. The inner strings are allocated
// first so the outer array's data block isn't bumped out from under us.
export function allocStringArray(h, items) {
    const innerPtrs = items.map((s) => allocString(h, s));
    const arrPtr = allocArray(h, items.length, 4);
    const data = arrData(h, arrPtr);
    for (let i = 0; i < innerPtrs.length; i++) {
        h.view.setInt32(data + i * 4, innerPtrs[i], true);
    }
    return arrPtr;
}
// String concat — allocate a fresh string holding a's bytes followed by
// b's. Backs the codegen-emitted `concat_strings` env import.
export function concatStrings(h, aPtr, bPtr) {
    const a = readBytesPtr(h, aPtr);
    const b = readBytesPtr(h, bPtr);
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return encodeBytes(h, out);
}
// String slice — allocate a fresh string holding the bytes in the half-open
// range [start, end). Backs the `substring` builtin. Byte-indexed to match
// `s[i]` / `len(s)`. Both bounds are clamped into [0, len] and an empty
// string is returned whenever end <= start, so inverted or out-of-range
// indices are total (never trap, never read past the data block).
export function subString(h, sPtr, start, end) {
    const bytes = readBytesPtr(h, sPtr);
    const n = bytes.length;
    let a = start | 0;
    let b = end | 0;
    if (a < 0)
        a = 0;
    else if (a > n)
        a = n;
    if (b < 0)
        b = 0;
    else if (b > n)
        b = n;
    if (b <= a)
        return encodeBytes(h, new Uint8Array(0));
    return encodeBytes(h, bytes.subarray(a, b));
}
// Truncate — allocate a fresh string holding the first min(len, n) bytes.
// Backs the codegen-emitted `truncate_string` env import behind the
// `truncate(s, N)` op: the programmer's explicit opt-in to lossy narrowing
// (the checker rejects an implicit store that could overflow). `n` is
// clamped at 0, so a negative cap yields the empty string rather than
// reading past the data block.
export function truncateString(h, sPtr, n) {
    const bytes = readBytesPtr(h, sPtr);
    let cap = n | 0;
    if (cap < 0)
        cap = 0;
    const end = cap < bytes.length ? cap : bytes.length;
    return encodeBytes(h, bytes.subarray(0, end));
}
// Reserve a string slot at a declared capacity: allocate a fresh string whose
// *capacity* (header `size`) is `capacity` bytes but whose live `length` is the
// source's, copying the source bytes in. Backs the codegen-emitted
// `string_reserve` import, emitted where a value is materialized into a bounded
// `string<N>` variable (docs/memory-model.md §"Strings"). Two consequences:
// (1) the variable owns a private buffer instead of aliasing a shared static
// literal, so it is safe to mutate/reuse in place; (2) every `string<N>` value
// is the same size, so when one escapes to a global it recycles in the escaped
// free list regardless of its content length. The store that put the value here
// already passed the checker's `sourceCap <= N` gate, so the source never
// exceeds `capacity`; clamp defensively all the same.
export function reserveString(h, srcPtr, capacity) {
    const bytes = readBytesPtr(h, srcPtr); // a copy — safe across the alloc below
    let cap = capacity | 0;
    if (cap < 0)
        cap = 0;
    const len = bytes.length < cap ? bytes.length : cap;
    const ptr = allocArray(h, len, 1, cap); // length = len, capacity = cap, zero-filled
    const data = arrData(h, ptr);
    for (let i = 0; i < len; i++)
        h.view.setUint8(data + i, bytes[i]);
    return ptr;
}
// ---- Inline string-array cells (`string<N>[M]`) ------------------------
//
// A bounded-string array stores its elements INLINE: M fixed-size cells laid
// out flat in the array's data block, each cell a 4-byte little-endian length
// prefix followed by N data bytes (stride `stringCellStride(N)` in layout.ts,
// which owns the +4/alignment convention). No per-element allocation, no
// pointers — the whole array is one object, promoted/reclaimed as a unit.
// Element access marshals at the boundary: a read copies the cell OUT to a
// fresh standalone string; a write copies the source bytes IN, clamped to N.
// Cells are deliberately not header-shaped, so a read never yields an interior
// pointer a write-barrier could mis-promote.
// Copy the inline string cell at `cellAddr` out into a fresh young string,
// returning its header pointer. The result is its own allocation (length = size
// = the cell's live length), so it behaves like any other string value — safe
// to concat, store, or promote. Backs the `string_cell_read` import.
export function readStringCell(h, cellAddr) {
    const len = h.view.getInt32(cellAddr, true);
    const src = cellAddr + 4;
    const out = allocArray(h, len, 1); // young string, length = size = len
    const data = arrData(h, out);
    // allocArray may have grown (and rebound) the view; cellAddr/src/data are
    // absolute offsets, so re-reading through h.view is safe.
    for (let i = 0; i < len; i++)
        h.view.setUint8(data + i, h.view.getUint8(src + i));
    return out;
}
// Copy `srcPtr`'s bytes into the inline string cell at `cellAddr`, clamped to
// `capacity` (N) bytes, updating the cell's length prefix. No allocation — the
// bytes land in the array's own (persistent) data block, so the young source
// can be rewound freely afterward (its content is already copied out). The
// checker's `sourceCap <= N` gate means the source never exceeds N; clamp
// defensively all the same. Backs the `string_cell_write` import.
export function writeStringCell(h, cellAddr, srcPtr, capacity) {
    const bytes = readBytesPtr(h, srcPtr); // a copy, taken before any store
    let cap = capacity | 0;
    if (cap < 0)
        cap = 0;
    const len = bytes.length < cap ? bytes.length : cap;
    h.view.setInt32(cellAddr, len, true);
    const data = cellAddr + 4;
    for (let i = 0; i < len; i++)
        h.view.setUint8(data + i, bytes[i]);
}
// Number→string — render an i32 as decimal text and allocate a fresh
// string. Backs the codegen-emitted `int_to_string` / `uint_to_string` env
// imports: a mixed `string + number` (either operand order) wraps its
// integer side in one of these (chosen by the operand's declared
// signedness) and then routes both sides through concat_strings.
// `intToString` reads the i32 as signed; `uintToString` reads the same bits
// as an unsigned 32-bit value, so a u32 above 2^31 renders as a large
// positive rather than negative. Mirrors print_i32 / print_u32.
export function intToString(h, n) {
    return allocString(h, String(n | 0));
}
export function uintToString(h, n) {
    return allocString(h, String(n >>> 0));
}
// String equality — byte-wise content comparison, returning 1 (equal) or 0.
// Backs the codegen-emitted `equal_strings` env import: `string == string`
// (and `!=`) must compare CONTENT, not the two string pointers (the checker
// documents string equality as byte-equal). Two distinct allocations with
// the same bytes — e.g. a host-returned hash vs a string literal — compare
// equal.
export function equalStrings(h, aPtr, bPtr) {
    const a = readBytesPtr(h, aPtr);
    const b = readBytesPtr(h, bPtr);
    if (a.length !== b.length)
        return 0;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i])
            return 0;
    }
    return 1;
}
// ---- Conditional packing: the layout "bytecode" ------------------------
//
// A widths array is normally all field bit-widths (1..32). These NEGATIVE
// sentinels turn it into a tiny layout program: a gate tests the FIELD
// IMMEDIATELY BEFORE it, and the fields up to the matching OP_END ride the
// wire only when the test passes. The VALUES array (pack) and the decoded
// output (unpack) always carry EVERY field, positional — a gated-off field
// simply contributes no bytes on pack and decodes as 0 on unpack. Only the
// byte stream shrinks. These values MUST match the script-side PACK_* consts.
// Exported so tests (and any tooling) share one source of truth; the
// script-side PACK_* consts must mirror these exact values.
export const OP_IF_NONZERO = -1; // include the branch iff the preceding field != 0
export const OP_IF_ZERO = -2; // include the branch iff the preceding field == 0
export const OP_IF_EQ = -3; // include iff preceding field == the NEXT entry (a literal)
export const OP_END = -4; // close the branch opened by the last OP_IF_*
const isField = (x) => x >= 0;
// From an IF's body start, return the index of its matching OP_END
// (nesting-aware), or -1 if the branch is unbalanced.
function matchEnd(widths, bodyStart) {
    let depth = 0;
    let w = bodyStart;
    while (w < widths.length) {
        const x = widths[w];
        if (isField(x)) {
            w++;
        }
        else if (x === OP_END) {
            if (depth === 0)
                return w;
            depth--;
            w++;
        }
        else {
            depth++;
            w += x === OP_IF_EQ ? 2 : 1;
        } // nested IF (+ its comparand)
    }
    return -1;
}
// Count field entries in [start, end) across every nesting level — skipping a
// branch skips ALL the fields it contains. An IF_EQ comparand is not a field.
function countFields(widths, start, end) {
    let n = 0;
    let w = start;
    while (w < end) {
        const x = widths[w];
        if (isField(x)) {
            n++;
            w++;
        }
        else if (x === OP_IF_EQ) {
            w += 2;
        }
        else {
            w++;
        }
    }
    return n;
}
// True iff the branch structure is well-formed: every OP_IF_* opens a branch a
// matching OP_END closes, with no stray OP_END. An UNbalanced layout makes
// packFields/unpackFields hit `matchEnd === -1` and `break` mid-walk, silently
// truncating the output — so packBits/unpackBits reject it up front (surfacing
// an error) rather than emitting a plausible-but-wrong partial. Mirrors
// matchEnd/countFields' walk (an OP_IF_EQ carries a literal comparand that is
// skipped, never interpreted as an opcode).
function isBalancedLayout(widths) {
    let depth = 0;
    let w = 0;
    while (w < widths.length) {
        const x = widths[w];
        if (isField(x)) {
            w++;
        }
        else if (x === OP_END) {
            if (depth === 0)
                return false;
            depth--;
            w++;
        }
        else if (x === OP_IF_EQ) {
            depth++;
            w += 2;
        } // opcode + its literal comparand
        else {
            depth++;
            w++;
        } // OP_IF_NONZERO / OP_IF_ZERO
    }
    return depth === 0;
}
// A field holds an i32 value, so its bit width is only meaningful in 1..32.
// Widths come straight from a script-supplied array; without this bound an
// oversized width (e.g. 2_000_000_000 — a perfectly valid positive i32 that
// passes isField/isBalancedLayout) makes packFields allocate ~totalBits/8
// bytes and spin a per-field loop billions of times in HOST code, which the
// wasm fuel budget does not meter — a resource-exhaustion escape from the
// sandbox (docs/trust-model.md). Called after isBalancedLayout so the walk is
// well-formed; mirrors countFields so an OP_IF_EQ comparand (any i32, e.g. a
// gate `field == 255`) is stepped over and never mistaken for a width.
const MAX_FIELD_BITS = 32;
function fieldWidthsInRange(widths) {
    let w = 0;
    while (w < widths.length) {
        const x = widths[w];
        if (isField(x)) {
            if (x < 1 || x > MAX_FIELD_BITS)
                return false;
            w++;
        }
        else if (x === OP_IF_EQ) {
            w += 2; // opcode + its literal comparand — comparand is not a width
        }
        else {
            w++; // OP_IF_NONZERO / OP_IF_ZERO / OP_END
        }
    }
    return true;
}
const gateTaken = (op, lastField, comparand) => op === OP_IF_NONZERO ? lastField !== 0
    : op === OP_IF_ZERO ? lastField === 0
        : op === OP_IF_EQ ? lastField === comparand
            : true;
// packFields(values, widths) — the pure bit-packer core: walk the (possibly
// gated) layout, emit only the fields the gates admit, and bit-pack them
// LSB-first into a byte array. Exported so the host `pack` builtin (packBits)
// AND tests share ONE implementation of the gate semantics rather than
// re-deriving the layout by hand.
export function packFields(values, widths) {
    const emitted = [];
    let v = 0;
    const walk = (start, end) => {
        let w = start;
        let lastField = 0;
        while (w < end) {
            const x = widths[w];
            if (isField(x)) {
                const val = values[v++] ?? 0;
                emitted.push({ value: val, width: x });
                lastField = val;
                w++;
            }
            else {
                const comparand = x === OP_IF_EQ ? widths[w + 1] : 0;
                const bodyStart = x === OP_IF_EQ ? w + 2 : w + 1;
                const endIdx = matchEnd(widths, bodyStart);
                if (endIdx < 0) {
                    break;
                } // unbalanced branch: stop here
                if (gateTaken(x, lastField, comparand)) {
                    walk(bodyStart, endIdx);
                }
                else {
                    v += countFields(widths, bodyStart, endIdx); // consume values, emit nothing
                }
                w = endIdx + 1;
            }
        }
    };
    walk(0, widths.length);
    const totalBits = emitted.reduce((a, e) => a + e.width, 0);
    const out = new Uint8Array(Math.ceil(totalBits / 8));
    let bitPos = 0;
    for (const { value, width } of emitted) {
        const mask = width >= 32 ? 0xFFFFFFFF | 0 : (1 << width) - 1;
        const v2 = value & mask;
        for (let b = 0; b < width; b++) {
            // Values are i32, so bits at position >= 32 are always 0. Emit them
            // explicitly rather than via `v >> b` — JS shift is mod-32, so
            // `v >> 32` would re-emit bit 0 and corrupt fields wider than 32 bits.
            const bit = b < 32 ? (v2 >>> b) & 1 : 0;
            const byteIdx = (bitPos + b) >> 3;
            const bitInByte = (bitPos + b) & 7;
            out[byteIdx] = (out[byteIdx] ?? 0) | (bit << bitInByte);
        }
        bitPos += width;
    }
    return out;
}
// pack(values, widths): the host builtin — reads the two Munos arrays off the
// heap, validates the layout, packs via packFields, and writes the bytes back
// as a Munos u8[]. `onError(reason)` is called (and an empty byte array
// returned) when the layout is rejected: either an UNBALANCED branch structure
// (an OP_IF_* without a matching OP_END — which would otherwise `break`
// mid-walk and silently truncate), or a value count that doesn't match the
// field count in `widths` (with gates the widths array is longer by its opcode
// entries).
export function packBits(h, valuesPtr, widthsPtr, onError) {
    const vLen = arrLength(h, valuesPtr);
    const wLen = arrLength(h, widthsPtr);
    const valuesData = arrData(h, valuesPtr);
    const widthsData = arrData(h, widthsPtr);
    const values = [];
    const widths = [];
    for (let i = 0; i < vLen; i++)
        values.push(h.view.getInt32(valuesData + i * 4, true));
    for (let i = 0; i < wLen; i++)
        widths.push(h.view.getInt32(widthsData + i * 4, true));
    if (!isBalancedLayout(widths)) {
        onError("unbalanced branch layout");
        return allocArray(h, 0, 1);
    }
    if (!fieldWidthsInRange(widths)) {
        onError("field width out of range (1..32)");
        return allocArray(h, 0, 1);
    }
    if (vLen !== countFields(widths, 0, wLen)) {
        onError("values/widths length mismatch");
        return allocArray(h, 0, 1);
    }
    const out = packFields(values, widths);
    const ptr = allocArray(h, out.length, 1);
    const outData = arrData(h, ptr);
    for (let i = 0; i < out.length; i++)
        h.view.setUint8(outData + i, out[i]);
    return ptr;
}
// unpackFields(bytes, widths) — the pure inverse of packFields: walk the same
// (possibly gated) layout, decoding one value per FIELD (a gated-off branch
// reads no bytes and its fields decode as 0, so the output stays positional — a
// `vals[i]` index is unaffected by whether a branch was taken). Exported so the
// host builtin and tests share one implementation.
export function unpackFields(bytes, widths) {
    const values = [];
    let bitPos = 0;
    const readField = (w) => {
        let v = 0;
        for (let b = 0; b < w; b++) {
            const byteIdx = (bitPos + b) >> 3;
            const bitInByte = (bitPos + b) & 7;
            const bit = ((bytes[byteIdx] ?? 0) >> bitInByte) & 1;
            // The decoded value is an i32; bits at position >= 32 can't be
            // represented (and `bit << 32` is `bit << 0`, which would corrupt the
            // low bits). The field still occupies all `w` bits on the wire —
            // bitPos advances by the full width below — we just drop the overflow.
            if (b < 32)
                v |= bit << b;
        }
        bitPos += w;
        return v;
    };
    const walk = (start, end) => {
        let w = start;
        let lastField = 0;
        while (w < end) {
            const x = widths[w];
            if (isField(x)) {
                const val = readField(x);
                values.push(val);
                lastField = val;
                w++;
            }
            else {
                const comparand = x === OP_IF_EQ ? widths[w + 1] : 0;
                const bodyStart = x === OP_IF_EQ ? w + 2 : w + 1;
                const endIdx = matchEnd(widths, bodyStart);
                if (endIdx < 0) {
                    break;
                } // unbalanced branch: stop here
                if (gateTaken(x, lastField, comparand)) {
                    walk(bodyStart, endIdx);
                }
                else {
                    const absent = countFields(widths, bodyStart, endIdx);
                    for (let k = 0; k < absent; k++)
                        values.push(0); // gated-off → 0, positional
                }
                w = endIdx + 1;
            }
        }
    };
    walk(0, widths.length);
    return values;
}
// unpack(bytes, widths): the host builtin — reads the two Munos arrays off the
// heap, decodes via unpackFields, and writes the values back as a Munos i32[].
// Mirrors packBits' up-front validation: an UNBALANCED branch layout (which
// unpackFields would otherwise silently truncate on) is rejected via
// `onError(reason)` and decodes to an empty array, rather than a
// plausible-but-wrong partial. `onError` is optional (the reference test host
// omits it); the real client/server hosts pass a logger.
export function unpackBits(h, bytesPtr, widthsPtr, onError) {
    const bLen = arrLength(h, bytesPtr);
    const wLen = arrLength(h, widthsPtr);
    const bytesData = arrData(h, bytesPtr);
    const widthsData = arrData(h, widthsPtr);
    const bytes = [];
    const widths = [];
    for (let i = 0; i < bLen; i++)
        bytes.push(h.view.getUint8(bytesData + i));
    for (let i = 0; i < wLen; i++)
        widths.push(h.view.getInt32(widthsData + i * 4, true));
    if (!isBalancedLayout(widths)) {
        onError?.("unbalanced branch layout");
        return allocArray(h, 0, 4);
    }
    if (!fieldWidthsInRange(widths)) {
        onError?.("field width out of range (1..32)");
        return allocArray(h, 0, 4);
    }
    const values = unpackFields(bytes, widths);
    const ptr = allocArray(h, values.length, 4);
    const outData = arrData(h, ptr);
    for (let i = 0; i < values.length; i++) {
        h.view.setInt32(outData + i * 4, values[i], true);
    }
    return ptr;
}
// Normalize a WebSocket "message" payload to a Uint8Array. `ws` delivers
// payloads as Buffer, Buffer[] (fragmented), ArrayBuffer, or string —
// callers want one shape. Strings are utf8-encoded; this is only a safety
// net since binary frames should never arrive as strings.
export function wsPayloadToUint8(raw) {
    if (raw instanceof Uint8Array)
        return raw;
    if (typeof Buffer !== "undefined" && Buffer.isBuffer(raw)) {
        return new Uint8Array(raw.buffer, raw.byteOffset, raw.byteLength);
    }
    if (Array.isArray(raw)) {
        const total = raw.reduce((s, b) => s + b.length, 0);
        const out = new Uint8Array(total);
        let off = 0;
        for (const b of raw) {
            out.set(b, off);
            off += b.length;
        }
        return out;
    }
    return new TextEncoder().encode(String(raw));
}
//# sourceMappingURL=memory-utils.js.map